import React from 'react';
import { motion } from 'framer-motion';
import { GiftIcon, CheckIcon } from 'lucide-react';
export function BonusSection() {
  return <section className="bg-cream py-16 md:py-24" aria-labelledby="bonus-heading" data-id="element-1" data-mp="1ofc3vpe5ijeu">
      <div className="max-w-5xl mx-auto px-4" data-id="element-2" data-mp="22f3fjjh73npo">
        {/* A. SECTION HEADER + TRANSITION COPY */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-3" data-mp="1yejh8ckvye1y">
          <h2 id="bonus-heading" className="font-serif text-forest text-3xl md:text-4xl lg:text-5xl font-700 italic mb-8" data-id="element-4" data-mp="1eqanvz7lw20v">
            "But I am Not Done Yet."
          </h2>
          <div className="font-sans text-stone-600 text-lg leading-relaxed max-w-3xl mx-auto space-y-4 text-left md:text-center" data-id="element-5" data-mp="mzeb0yljpxdl">
            <p data-id="element-6" data-mp="29rjvgswcslbx">
              When I put together <em data-id="element-7" data-mp="29q8o6j3p8vg2">The Cortisol Reset Kitchen</em>, I knew
              the protocol itself was powerful.
            </p>
            <p data-id="element-8" data-mp="2jhctss7hj3y8">But I also knew something else.</p>
            <p data-id="element-9" data-mp="2jio99paam056">
              I knew that the hardest moments for women over 55 are not the
              Monday mornings when motivation is high.
            </p>
            <p data-id="element-10" data-mp="1qu0k3imfaqmc">
              They are the{' '}
              <strong className="font-700 text-forest" data-id="element-11" data-mp="1qvbo9jjs60dz">Tuesday nights</strong>{' '}
              when everyone at the restaurant is ordering pasta and you do not
              know what to do.
            </p>
            <p data-id="element-12" data-mp="20trd1fvc4xkb">
              They are the{' '}
              <strong className="font-700 text-forest" data-id="element-13" data-mp="25rj3s6k8udmx">11 PM moments</strong>{' '}
              when your stomach is growling and every diet rule you know says:{' '}
              <em data-id="element-14" data-mp="3h5ekhjqsqug9">just ignore it.</em>
            </p>
            <p data-id="element-15" data-mp="3h43hvn937sqq">
              They are the{' '}
              <strong className="font-700 text-forest" data-id="element-16" data-mp="1h35b9yboqvyd">
                Wednesday mornings
              </strong>{' '}
              when your knees ache and you feel guilty for not doing cardio.
            </p>
            <p data-id="element-17" data-mp="1q4ljq0ixwnzy">
              So I built three companion guides to cover exactly those moments.
            </p>
            <p data-id="element-18" data-mp="1m3jjbgdae5we">
              And today, when you join <em data-id="element-19" data-mp="1m0dt1xsx8xz3">The Cortisol Reset Kitchen</em>, you
              get all three of them{' '}
              <strong className="font-700 text-forest" data-id="element-20" data-mp="yd5s1s2j0b2v">completely free.</strong>
            </p>
          </div>
        </motion.div>

        {/* B. BONUS #1 */}
        <motion.article initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-white rounded-2xl shadow-sm border border-sage/20 p-6 md:p-10 mb-12" data-id="element-21" data-mp="175hhbalwlsh9">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center" data-id="element-22" data-mp="id2bia8qn5u">
            <div className="order-2 md:order-1" data-id="element-23" data-mp="5aczj2yxf1yq">
              <img src="https://cdn.magicpatterns.com/uploads/jYW7AwDJmZK8DYTcxYwyMP/heylloyd-image-1773015601413_port_bono1.png" alt="The Restaurant Survival Blueprint" className="w-full rounded-2xl shadow-md" data-id="element-24" data-mp="5c7lmklxp04e" />
            </div>
            <div className="order-1 md:order-2" data-id="element-25" data-mp="janq04wbqejd">
              <div className="inline-flex items-center gap-2 bg-forest text-cream rounded-full px-4 py-1.5 mb-6" data-id="element-26" data-mp="o9iijyk3fhgo">
                <GiftIcon className="w-4 h-4 text-terracotta" aria-hidden="true" data-id="element-27" data-mp="9aoimbbn4u83" />
                <span className="font-sans text-xs font-700 tracking-widest uppercase" data-id="element-28" data-mp="e8ygwusnwaxx">
                  FREE BONUS #1 — Valued at $27
                </span>
              </div>
              <h3 className="font-serif text-forest text-3xl font-700 mb-2" data-id="element-29" data-mp="2z1tgybyvieh2">
                The Restaurant Survival Blueprint
              </h3>
              <p className="font-sans text-stone-500 italic text-lg mb-6" data-id="element-30" data-mp="3ahganrgx3kob">
                "How to Dine Out Without Triggering a 48-Hour Cortisol Bloat"
              </p>

              <div className="font-sans text-stone-600 space-y-4 mb-6" data-id="element-31" data-mp="35imv7ti2ljli">
                <p data-id="element-32" data-mp="30kldpd0alsc5">Most women over 55 quietly dread dinner invitations.</p>
                <p data-id="element-33" data-mp="226cgug5ezlt">
                  Not because they do not want the friendship or the laughter or
                  the glass of wine.
                </p>
                <p data-id="element-34" data-mp="3t5uzq97ocomh">But because they know what happens the next morning.</p>
                <p data-id="element-35" data-mp="3o79z48oig2xi">
                  The scale spikes. The waistband tightens. The bloating arrives
                  like clockwork. And they spend two days trying to undo one
                  dinner out.
                </p>
                <p data-id="element-36" data-mp="2crbklpakcoow">
                  <strong className="font-700 text-forest" data-id="element-37" data-mp="27uuy0zj0iidx">
                    Here is what nobody has told you: it is not the portion size
                    that is the problem.
                  </strong>
                </p>
                <p data-id="element-38" data-mp="scowx1e2pv4k">
                  It is the hidden cortisol triggers inside restaurant kitchens
                  — the seed oils, the sodium-loaded sauces, the blood sugar
                  roller coaster disguised as a healthy salad.
                </p>
              </div>

              <h4 className="font-sans font-700 text-forest text-lg mb-4" data-id="element-39" data-mp="1712wn3g713gw">
                The Restaurant Survival Blueprint gives you:
              </h4>
              <ul className="space-y-3 mb-6" data-id="element-40" data-mp="1b0btih6j5hgo">
                <li className="flex items-start gap-3" data-id="element-41" data-mp="1g0q2q2jvaxga">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-42" data-mp="1fz53w0qisscm" />
                  <span className="font-sans text-stone-600" data-id="element-43" data-mp="3htwcc1cw1fhp">
                    <strong className="font-700 text-forest" data-id="element-44" data-mp="3mrfnuti7be5y">
                      The 5-Second Menu Decoder
                    </strong>{' '}
                    — instantly identify the 3 ingredients that spike cortisol
                    at any restaurant
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-45" data-mp="3opp5pjo57jo3">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-46" data-mp="3oqgzcoy6j7bq" />
                  <span className="font-sans text-stone-600" data-id="element-47" data-mp="1jeq3s4nfudb">
                    <strong className="font-700 text-forest" data-id="element-48" data-mp="3to8wbxdoljms">
                      The Cuisine-by-Cuisine Safe List
                    </strong>{' '}
                    — Italian, Mexican, Asian, American. You will never feel
                    lost at a menu again
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-49" data-mp="l7xisjov5pwr">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-50" data-mp="l6comv6go8zf" />
                  <span className="font-sans text-stone-600" data-id="element-51" data-mp="zw7cub5fjm10">
                    <strong className="font-700 text-forest" data-id="element-52" data-mp="13whgvi9nw2d8">
                      The Anti-Inflammatory Bar Order
                    </strong>{' '}
                    — what to drink instead of sugary cocktails (and still feel
                    social)
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-53" data-mp="g7f2cjabqpmy">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-54" data-mp="g5vkio90s6j2" />
                  <span className="font-sans text-stone-600" data-id="element-55" data-mp="779vqbcyfv9u">
                    <strong className="font-700 text-forest" data-id="element-56" data-mp="c5l38ffs1tow">
                      The Morning-After Reset Protocol
                    </strong>{' '}
                    — wake up lighter even after a dinner out
                  </span>
                </li>
              </ul>

              <p className="font-sans text-stone-600 mb-6" data-id="element-57" data-mp="26m7tyk0vuju">
                <strong className="font-700 text-forest" data-id="element-58" data-mp="hodpbw5u335t">
                  The result: You go to dinner. You enjoy it. You wake up
                  feeling lighter than before you went.
                </strong>
              </p>

              <blockquote className="bg-forest/5 border-l-4 border-forest p-4 rounded-r-lg" data-id="element-59" data-mp="8mw0nped1t5i">
                <p className="font-serif text-forest italic text-lg" data-id="element-60" data-mp="3p476qy2hyz7">
                  "This guide alone is worth more than the entire investment
                  today."
                </p>
              </blockquote>
            </div>
          </div>
        </motion.article>

        {/* C. BONUS #2 */}
        <motion.article initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-white rounded-2xl shadow-sm border border-sage/20 p-6 md:p-10 mb-12" data-id="element-61" data-mp="3m3vbu7lwx1yq">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center" data-id="element-62" data-mp="2x4ih4i64bz8v">
            <div className="order-1 md:order-1" data-id="element-63" data-mp="31zxr2z109b00">
              <div className="inline-flex items-center gap-2 bg-forest text-cream rounded-full px-4 py-1.5 mb-6" data-id="element-64" data-mp="370vafexr4b33">
                <GiftIcon className="w-4 h-4 text-terracotta" aria-hidden="true" data-id="element-65" data-mp="3bzyfgzdqehb2" />
                <span className="font-sans text-xs font-700 tracking-widest uppercase" data-id="element-66" data-mp="2edhkw1wduht0">
                  FREE BONUS #2 — Valued at $19
                </span>
              </div>
              <h3 className="font-serif text-forest text-3xl font-700 mb-2" data-id="element-67" data-mp="2o92qsb4ko281">
                The 11 PM Emergency Snack List
              </h3>
              <p className="font-sans text-stone-500 italic text-lg mb-6" data-id="element-68" data-mp="2t2x50z1erozq">
                "12 Cortisol-Lowering Snacks That Help You Sleep Deeper and Wake
                Up Lighter"
              </p>

              <div className="font-sans text-stone-600 space-y-4 mb-6" data-id="element-69" data-mp="20i6rksxse4qr">
                <p data-id="element-70" data-mp="2uum4jxeqjjir">It is 11 PM.</p>
                <p data-id="element-71" data-mp="2zu8k4di1dbuq">Your stomach is making that quiet, insistent sound.</p>
                <p data-id="element-72" data-mp="2zse0d2ii3ps8">
                  Every diet you have ever followed says:{' '}
                  <em data-id="element-73" data-mp="34ttk8r8xzq91">drink water and go to bed.</em>
                </p>
                <p data-id="element-74" data-mp="38v6qv3juzxh0">
                  But here is what that advice is actually doing to your body.
                </p>
                <p data-id="element-75" data-mp="2c5xmrkbjtub7">
                  When you go to bed with low blood sugar, your cortisol surges
                  to compensate. That surge is the exact reason you wake up at 3
                  AM with a racing mind and a tight midsection.
                </p>
                <p data-id="element-76" data-mp="2pwkozmgiq2c1">
                  <strong className="font-700 text-forest" data-id="element-77" data-mp="2px2rv1xoresx">
                    Going to bed hungry is not discipline. It is a cortisol
                    event.
                  </strong>
                </p>
                <p data-id="element-78" data-mp="1xefbfztmwny6">
                  <em data-id="element-79" data-mp="1xdxa4os47jje">The 11 PM Emergency Snack List</em> gives you{' '}
                  <strong className="font-700 text-forest" data-id="element-80" data-mp="1pv5zpzxnod5y">
                    12 specific snacks
                  </strong>{' '}
                  that do the opposite — they lower cortisol, stabilize blood
                  sugar, and trigger the deep, fat-burning REM sleep your body
                  has been missing.
                </p>
              </div>

              <h4 className="font-sans font-700 text-forest text-lg mb-4" data-id="element-81" data-mp="16x395gpgm861">
                Inside this guide you will find:
              </h4>
              <ul className="space-y-3 mb-6" data-id="element-82" data-mp="125v04ls1kote">
                <li className="flex items-start gap-3" data-id="element-83" data-mp="x7jpi8txaaa4">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-84" data-mp="x8369m7u6smk" />
                  <span className="font-sans text-stone-600" data-id="element-85" data-mp="o7evd0dqp8dw">
                    <strong className="font-700 text-forest" data-id="element-86" data-mp="o8px6unmk62d">
                      The Safety Boat Snack
                    </strong>{' '}
                    — 1 ingredient, 60 seconds, signals your adrenal system to
                    stand down
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-87" data-mp="eh6ljx6c5hmp">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-88" data-mp="3vsfzt44jlsem" />
                  <span className="font-sans text-stone-600" data-id="element-89" data-mp="saxub3c8sxhd">
                    <strong className="font-700 text-forest" data-id="element-90" data-mp="sbfwegll65x7">
                      The Melatonin Precursor Combo
                    </strong>{' '}
                    — the exact nutrient pairing that puts your body into
                    fat-burning sleep mode
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-91" data-mp="nhuwgj0fn1vd">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-92" data-mp="1b8h4496iyk4u" />
                  <span className="font-sans text-stone-600" data-id="element-93" data-mp="30qe1adfo9uge">
                    <strong className="font-700 text-forest" data-id="element-94" data-mp="30ot7wr53ghk4">
                      The No-Go After-9 PM List
                    </strong>{' '}
                    — 3 foods that seem healthy but are secretly spiking your
                    cortisol all night
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-95" data-mp="35jgxktbu3i0a">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-96" data-mp="2hr05rr2aobns" />
                  <span className="font-sans text-stone-600" data-id="element-97" data-mp="2qshtnvly1hn1">
                    <strong className="font-700 text-forest" data-id="element-98" data-mp="2qu161b4hb8jj">
                      The 4-Step Cortisol Shutdown Protocol
                    </strong>{' '}
                    — your new nightly closing ritual
                  </span>
                </li>
              </ul>

              <p className="font-sans text-stone-600 mb-6" data-id="element-99" data-mp="1zlzllg5gubq">
                <strong className="font-700 text-forest" data-id="element-100" data-mp="xatx275x3z1o">
                  The result: You stop waking up at 3 AM. You stop feeling
                  guilty for being hungry. And you start waking up feeling
                  genuinely rested — and noticeably less puffy.
                </strong>
              </p>

              <blockquote className="bg-forest/5 border-l-4 border-forest p-4 rounded-r-lg" data-id="element-101" data-mp="1bb4nj93bfbmb">
                <p className="font-serif text-forest italic text-lg" data-id="element-102" data-mp="1g9y0n0eox0ly">
                  "This is the guide I wish someone had given me 10 years ago."
                </p>
              </blockquote>
            </div>
            <div className="order-2 md:order-2" data-id="element-103" data-mp="1p1hylkbi76fv">
              <img src="https://cdn.magicpatterns.com/uploads/nrkvf634uKhnnZZD2g4UAK/heylloyd-image-1773015622862_port_bono2.png" alt="The 11 PM Emergency Snack List" className="w-full rounded-2xl shadow-md" data-id="element-104" data-mp="1p430g9bsxmdm" />
            </div>
          </div>
        </motion.article>

        {/* D. BONUS #3 */}
        <motion.article initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-white rounded-2xl shadow-sm border border-sage/20 p-6 md:p-10 mb-16" data-id="element-105" data-mp="3ohl0sf1r830i">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center" data-id="element-106" data-mp="b1ddelhk5nn0">
            <div className="order-2 md:order-1" data-id="element-107" data-mp="62sf4rlv9e17">
              <img src="https://cdn.magicpatterns.com/uploads/mMtKQtynhoa89sdwDdop6t/heylloyd-image-1773015694376_port_bono_3.png" alt="The 7-Minute Metabolic Spark" className="w-full rounded-2xl shadow-md" data-id="element-108" data-mp="65euveiwuzui" />
            </div>
            <div className="order-1 md:order-2" data-id="element-109" data-mp="tuhcimdjosp0">
              <div className="inline-flex items-center gap-2 bg-forest text-cream rounded-full px-4 py-1.5 mb-6" data-id="element-110" data-mp="p1o51qqoz698">
                <GiftIcon className="w-4 h-4 text-terracotta" aria-hidden="true" data-id="element-111" data-mp="2j9rzej8f39en" />
                <span className="font-sans text-xs font-700 tracking-widest uppercase" data-id="element-112" data-mp="2o8blcjn0gl70">
                  FREE BONUS #3 — Valued at $29
                </span>
              </div>
              <h3 className="font-serif text-forest text-3xl font-700 mb-2" data-id="element-113" data-mp="2ei0il0f2ci1p">
                The 7-Minute Metabolic Spark
              </h3>
              <p className="font-sans text-stone-500 italic text-lg mb-6" data-id="element-114" data-mp="20lws9i7b8tui">
                "The Joint-Safe Morning Routine That Wakes Up Your Thyroid
                Without Spiking Your Stress Hormones"
              </p>

              <div className="font-sans text-stone-600 space-y-4 mb-6" data-id="element-115" data-mp="1vk7cjp957s03">
                <p data-id="element-116" data-mp="1vmaf18rhfdlp">
                  You already know that long cardio sessions are working against
                  you.
                </p>
                <p data-id="element-117" data-mp="3pyqi60ozebmc">
                  <strong className="font-700 text-forest" data-id="element-118" data-mp="3px5jbyvmw6io">
                    But here is the question nobody answers: if not that, then
                    what?
                  </strong>
                </p>
                <p data-id="element-119" data-mp="19dv7zvzsdwqv">The answer is not another gym membership.</p>
                <p data-id="element-120" data-mp="1o16h7r8hx4nt">
                  It is not a YouTube workout that has you jumping around at 6
                  AM.
                </p>
                <p data-id="element-121" data-mp="1j8wx4ru2aytg">It is 7 minutes. In your pajamas. Barefoot, if you want.</p>
                <p data-id="element-122" data-mp="1x0be6t4espxv">
                  <strong className="font-700 text-forest" data-id="element-123" data-mp="1wzs0jolsknpn">
                    The 7-Minute Metabolic Spark
                  </strong>{' '}
                  is a cortisol-conscious movement protocol designed
                  specifically for women over 55 — to activate your largest
                  muscle groups, stimulate lymphatic drainage, and send a clear
                  signal to your thyroid: <em data-id="element-124" data-mp="cm1e4m4gd43o">It is safe to burn now.</em>
                </p>
                <p data-id="element-125" data-mp="7nq4abe5qtlg">No jumping. No equipment. No joint pain.</p>
              </div>

              <h4 className="font-sans font-700 text-forest text-lg mb-4" data-id="element-126" data-mp="3hzf09ekpifwq">
                Inside this guide you will find:
              </h4>
              <ul className="space-y-3 mb-6" data-id="element-127" data-mp="3mwf21daiwwi1">
                <li className="flex items-start gap-3" data-id="element-128" data-mp="3roykooo2ihtp">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-129" data-mp="3rphzvxmc2s41" />
                  <span className="font-sans text-stone-600" data-id="element-130" data-mp="2z12odl2eurxg">
                    <strong className="font-700 text-forest" data-id="element-131" data-mp="33z48887nuv9z">
                      The 4-Movement Release Circuit
                    </strong>{' '}
                    — 90 seconds each, zero impact, done in any room of your
                    house
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-132" data-mp="o7zurxxy3z7b">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-133" data-mp="o780cqg347im" />
                  <span className="font-sans text-stone-600" data-id="element-134" data-mp="2cs5akhqffegg">
                    <strong className="font-700 text-forest" data-id="element-135" data-mp="2ctqa6lrllnl6">
                      The 2 Thermal Window Timing Slots
                    </strong>{' '}
                    — the exact moments of day when these 7 minutes burn 3x more
                    effectively
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-136" data-mp="1yyeadzyl56xe">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-137" data-mp="1u1xdohdblk8p" />
                  <span className="font-sans text-stone-600" data-id="element-138" data-mp="1kzxupjiqw634">
                    <strong className="font-700 text-forest" data-id="element-139" data-mp="1l2k4lpt3yxoi">
                      The Exhale Rhythm Rule
                    </strong>{' '}
                    — the one signal that tells you if you are helping or
                    hurting your cortisol levels
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-140" data-mp="3adjkhp11q7r1">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-141" data-mp="7j9lqizjdgfg" />
                  <span className="font-sans text-stone-600" data-id="element-142" data-mp="3tzm6ruz4jftx">
                    <strong className="font-700 text-forest" data-id="element-143" data-mp="3ty2o5xpzwsoz">
                      The Energy Check-In Guide
                    </strong>{' '}
                    — how to know, within 60 seconds of finishing, if your body
                    is responding
                  </span>
                </li>
              </ul>

              <p className="font-sans text-stone-600 mb-6" data-id="element-144" data-mp="3b19yscjy8bzx">
                <strong className="font-700 text-forest" data-id="element-145" data-mp="3b1tcfh2kge85">
                  The result: You move your body without punishing it. Your
                  midsection starts to feel less armored. And for the first time
                  in years, morning feels like a gift instead of a battle.
                </strong>
              </p>

              <blockquote className="bg-forest/5 border-l-4 border-forest p-4 rounded-r-lg" data-id="element-146" data-mp="1bucolkzgs5r6">
                <p className="font-serif text-forest italic text-lg" data-id="element-147" data-mp="1bsib2rqivfx4">
                  "Seven minutes. That is all it takes to change what your body
                  does for the next 17 hours."
                </p>
              </blockquote>
            </div>
          </div>
        </motion.article>

        {/* E. BONUS STACK SUMMARY TABLE */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-sage/20 overflow-hidden mb-16" data-id="element-148" data-mp="swjxrh247ma5">
          <div className="bg-forest px-6 py-4 text-center" data-id="element-149" data-mp="2e8jy71qp3h6z">
            <h3 className="font-serif text-cream text-xl font-700" data-id="element-150" data-mp="2j750d6picay2">
              Bonus Summary
            </h3>
          </div>
          <div className="p-6" data-id="element-151" data-mp="j43rnzdohukm">
            <div className="space-y-4" data-id="element-152" data-mp="j7hxjhljwlwt">
              <div className="flex items-center justify-between border-b border-sage/20 pb-4" data-id="element-153" data-mp="e7dbzd5sd0zq">
                <div className="flex items-center gap-3" data-id="element-154" data-mp="s6muwkldm97e">
                  <GiftIcon className="w-5 h-5 text-terracotta" aria-hidden="true" data-id="element-155" data-mp="s40l0eb0jhm0" />
                  <span className="font-sans text-stone-700 font-700" data-id="element-156" data-mp="edvp8y6j2r4">
                    The Restaurant Survival Blueprint
                  </span>
                </div>
                <div className="flex items-center gap-4 text-right" data-id="element-157" data-mp="978upkx2028g">
                  <span className="font-sans text-stone-400 line-through hidden sm:inline" data-id="element-158" data-mp="99lm0oopboy6">
                    $27
                  </span>
                  <span className="font-sans text-terracotta font-700" data-id="element-159" data-mp="3cw2eaui9mvn0">
                    FREE
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between border-b border-sage/20 pb-4" data-id="element-160" data-mp="38swbjwud3mla">
                <div className="flex items-center gap-3" data-id="element-161" data-mp="2yoz3vsgqdfib">
                  <GiftIcon className="w-5 h-5 text-terracotta" aria-hidden="true" data-id="element-162" data-mp="33itw5kaej2t0" />
                  <span className="font-sans text-stone-700 font-700" data-id="element-163" data-mp="2oqrsdp57npnp">
                    The 11 PM Emergency Snack List
                  </span>
                </div>
                <div className="flex items-center gap-4 text-right" data-id="element-164" data-mp="3hiux0zqo2a8h">
                  <span className="font-sans text-stone-400 line-through hidden sm:inline" data-id="element-165" data-mp="3mh66h9d1uiq6">
                    $19
                  </span>
                  <span className="font-sans text-terracotta font-700" data-id="element-166" data-mp="37iue3ewplw4t">
                    FREE
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between border-b border-sage/20 pb-4" data-id="element-167" data-mp="1w3xj7odugcpj">
                <div className="flex items-center gap-3" data-id="element-168" data-mp="213tf10ce0xu0">
                  <GiftIcon className="w-5 h-5 text-terracotta" aria-hidden="true" data-id="element-169" data-mp="2117892xbmmcu" />
                  <span className="font-sans text-stone-700 font-700" data-id="element-170" data-mp="2cjg4g3nefhvg">
                    The 7-Minute Metabolic Spark
                  </span>
                </div>
                <div className="flex items-center gap-4 text-right" data-id="element-171" data-mp="22ljwfkprd31k">
                  <span className="font-sans text-stone-400 line-through hidden sm:inline" data-id="element-172" data-mp="22nd4n4g0rvbo">
                    $29
                  </span>
                  <span className="font-sans text-terracotta font-700" data-id="element-173" data-mp="2vah2mpgp39dy">
                    FREE
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between pt-2" data-id="element-174" data-mp="2q99weuu7ai9e">
                <span className="font-sans text-forest font-700 text-lg" data-id="element-175" data-mp="2lb01mlce1job">
                  Total Bonus Value
                </span>
                <div className="flex items-center gap-4 text-right" data-id="element-176" data-mp="1etux3keireb4">
                  <span className="font-sans text-stone-400 line-through hidden sm:inline" data-id="element-177" data-mp="19w3931gzwcc7">
                    $75
                  </span>
                  <span className="font-sans text-terracotta font-700 text-xl" data-id="element-178" data-mp="3lidgayzjztfj">
                    FREE
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* F. CLOSING TRANSITION COPY */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto font-sans text-stone-600 text-lg leading-relaxed space-y-4" data-id="element-179" data-mp="45b1muhbtuo2">
          <p data-id="element-180" data-mp="n0hvtttbpj1n">So let us talk about what you are actually getting today.</p>
          <p data-id="element-181" data-mp="2jwfdx1fb2kp5">
            You are getting <em data-id="element-182" data-mp="2jx794b4zqgew">The Cortisol Reset Kitchen</em> — the complete
            21-day protocol to reset your stress hormones, flatten your
            midsection, and finally feel at home in your body again.
          </p>
          <p data-id="element-183" data-mp="2crxrcbqsgup5">
            <strong className="font-700 text-forest" data-id="element-184" data-mp="2cs62nz79jry8">
              You are getting $75 worth of companion guides
            </strong>{' '}
            that cover the three hardest real-life moments women over 55 face
            every single week.
          </p>
          <p data-id="element-185" data-mp="2lufc0lqspfa1">
            And you are getting all of it for less than the cost of a single
            dinner out.
          </p>
        </motion.div>
      </div>
    </section>;
}